import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FORMAT_OPTIONS } from '../../features/writerProfile/options';

interface Story {
  id: string;
  title: string;
  format: string;
  genres: string[];
  status: string;
  wordCount: number;
  targetLength: string | null;
  updatedAt: string;
  role?: 'owner' | 'editor' | 'viewer';
  /** Set on stories someone else owns and shared with this writer. */
  ownerName?: string | null;
}

const statusColors: Record<string, string> = {
  planning: 'bg-blue-500/20 text-blue-400',
  writing: 'bg-green-500/20 text-green-400',
  revising: 'bg-yellow-500/20 text-yellow-400',
  complete: 'bg-purple-500/20 text-purple-400',
};

const StoryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const res = await axios.get('/stories');
      setStories(res.data);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatLabel = (format: string) => {
    const option = FORMAT_OPTIONS.find((o) => o.value === format);
    return option ? `${option.emoji} ${t(option.key, option.label)}` : format;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <MobileLayout headerTitle={t('stories.title', 'Stories')}>
      <div className="flex-1 flex flex-col p-6">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
            />
          </div>
        ) : stories.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <BookOpenIcon
              className="w-16 h-16 mb-4"
              style={{ color: 'var(--ds-text-secondary)' }}
            />
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--ds-text-primary)' }}>
              {t('stories.emptyTitle', 'No stories yet')}
            </h2>
            <p className="mb-6" style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'stories.emptyDescription',
                'Start your first story and bring your ideas to life.',
              )}
            </p>
            <button
              onClick={() => navigate('/stories/new')}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-colors"
            >
              {t('stories.createFirst', 'Create Your First Story')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* New Story Card */}
            <button
              onClick={() => navigate('/stories/new')}
              className="ds-card rounded-2xl p-6 flex flex-col items-center justify-center min-h-[180px] border-2 border-dashed transition-colors hover:border-primary-500"
              style={{
                borderColor: 'var(--ds-border)',
                background: 'var(--ds-bg-card)',
              }}
            >
              <PlusIcon className="w-10 h-10 mb-2" style={{ color: 'var(--ds-text-secondary)' }} />
              <span className="font-semibold" style={{ color: 'var(--ds-text-secondary)' }}>
                {t('stories.newStory', 'New Story')}
              </span>
            </button>

            {stories.map((story) => (
              <button
                key={story.id}
                onClick={() => navigate(`/stories/${story.id}`)}
                className="ds-card rounded-2xl p-5 text-left transition-all hover:scale-[1.02]"
                style={{
                  background: 'var(--ds-bg-card)',
                  border: '1px solid var(--ds-border)',
                }}
              >
                <h3
                  className="text-lg font-semibold mb-2 truncate"
                  style={{ color: 'var(--ds-text-primary)' }}
                >
                  {story.title}
                </h3>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      background: 'var(--ds-tint)',
                      color: 'var(--ds-tint-text)',
                    }}
                  >
                    {formatLabel(story.format)}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${statusColors[story.status] || ''}`}
                  >
                    {t(`stories.status.${story.status}`, story.status)}
                  </span>
                </div>
                {story.role && story.role !== 'owner' && (
                  <div
                    className="text-xs mb-2 truncate"
                    style={{ color: 'var(--ds-text-secondary)' }}
                  >
                    {t('stories.members.sharedBy', 'Shared by {{name}}', {
                      name: story.ownerName || t('stories.members.unnamed', 'Unnamed writer'),
                    })}{' '}
                    ·{' '}
                    {story.role === 'editor'
                      ? t('stories.members.roleEditor', 'Editor')
                      : t('stories.members.roleViewer', 'Viewer')}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mb-3">
                  {story.genres?.slice(0, 3).map((genre) => (
                    <span
                      key={genre}
                      className="text-[11px] px-2 py-0.5 rounded-full"
                      style={{
                        background: 'var(--ds-border)',
                        color: 'var(--ds-text-secondary)',
                      }}
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <div
                  className="flex items-center justify-between text-xs"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  <span>
                    {story.wordCount.toLocaleString()} {t('stories.words', 'words')}
                  </span>
                  <span>{formatDate(story.updatedAt)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default StoryDashboard;
